import Button from "../../../shared/components/Button";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import type { DraftPlayer } from "../../challenges/types/challengeDraft";
import { normalizeCpf } from "../../challenges/utils/challengePairs";
import {
  formatPaymentStatusLabel,
  formatRegistrationStatusLabel,
} from "../../events/shared/utils/eventRegistrationFormatters";
import type { AdminEventRegistration } from "../services/eventRegistrationAdminService";
import AdminRegistrationRowActions from "./AdminRegistrationRowActions";

type PaymentMethod = "pix" | "credit_card" | "debit_card";

type AdminSuper8GroupsProps = {
  adminPaymentMethod: PaymentMethod;
  groupAPlayers: DraftPlayer[];
  isAdmin: boolean;
  onCopyPaymentLink: (
    id: number,
    paymentMethod?: PaymentMethod,
    existingPaymentLink?: string | null,
  ) => void;
  onDeleteRegistration: (
    id: number,
    registration?: AdminEventRegistration | null,
  ) => void;
  onEditAvailabilityRestriction?: (registration: AdminEventRegistration) => void;
  onMarkPaidRegistration: (
    id: number,
    registration?: AdminEventRegistration | null,
  ) => void;
  onRejectRegistration: (
    id: number,
    registration?: AdminEventRegistration | null,
  ) => void;
  onRemovePlayer: (cpf: string) => void;
  registrationByCpf: Record<string, AdminEventRegistration>;
  waitingPlayers: DraftPlayer[];
};

function AdminSuper8PlayerRow({
  adminPaymentMethod,
  isAdmin,
  onCopyPaymentLink,
  onDeleteRegistration,
  onEditAvailabilityRestriction,
  onMarkPaidRegistration,
  onRejectRegistration,
  onRemovePlayer,
  player,
  registration,
}: {
  adminPaymentMethod: PaymentMethod;
  isAdmin: boolean;
  onCopyPaymentLink: AdminSuper8GroupsProps["onCopyPaymentLink"];
  onDeleteRegistration: AdminSuper8GroupsProps["onDeleteRegistration"];
  onEditAvailabilityRestriction: AdminSuper8GroupsProps["onEditAvailabilityRestriction"];
  onMarkPaidRegistration: AdminSuper8GroupsProps["onMarkPaidRegistration"];
  onRejectRegistration: AdminSuper8GroupsProps["onRejectRegistration"];
  onRemovePlayer: AdminSuper8GroupsProps["onRemovePlayer"];
  player: DraftPlayer;
  registration: AdminEventRegistration | null;
}) {
  const t = siteTexts.admin;
  const restrictionSummary = (registration: AdminEventRegistration) => {
    const details = registration.availabilityRestriction?.details || [];
    const labels = details
      .map((item) => String(item.label || "").trim())
      .filter(Boolean);
    const fallback = String(registration.availabilityRestriction?.label || "").trim();
    const summary = labels.length > 0 ? labels.join(" • ") : fallback;
    return registration.availabilityRestriction?.hasRestriction && summary
      ? summary
      : "";
  };

  return (
    <div className="super8-group-row">
      <div className="challenge-registration-row-main">
        <span>{player.name}</span>
        {registration && (
          <>
            <p className="challenge-registration-row-status">
              {t.eventLink.registration}:{" "}
              <strong>{formatRegistrationStatusLabel(registration.status)}</strong>
              {" · "}
              {t.registrationsTab.payment}:{" "}
              <strong>
                {registration.paymentRequired
                  ? formatPaymentStatusLabel(registration.paymentStatus)
                  : t.registrationsTab.notRequired}
              </strong>
            </p>
            {restrictionSummary(registration) && (
              <p className="challenge-registration-row-status challenge-registration-row-status--restriction">
                Restrição: <strong>{restrictionSummary(registration)}</strong>
              </p>
            )}
          </>
        )}
      </div>
      {registration ? (
        <AdminRegistrationRowActions
          adminPaymentMethod={adminPaymentMethod}
          isAdmin={isAdmin}
          onCopyPaymentLink={onCopyPaymentLink}
          onDeleteRegistration={onDeleteRegistration}
          onEditAvailabilityRestriction={onEditAvailabilityRestriction}
          onMarkPaidRegistration={onMarkPaidRegistration}
          onRejectRegistration={onRejectRegistration}
          registration={registration}
          showCancelAction
        />
      ) : (
        <div className="challenge-registration-row-actions">
          <Button type="button" variant="pill" disabled>
            Processando inscrição...
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={() => onRemovePlayer(player.cpf)}
          >
            {siteTexts.challengeEngine.labels.remove}
          </Button>
        </div>
      )}
    </div>
  );
}

function AdminSuper8GroupCard({
  adminPaymentMethod,
  isAdmin,
  onCopyPaymentLink,
  onDeleteRegistration,
  onEditAvailabilityRestriction,
  onMarkPaidRegistration,
  onRejectRegistration,
  onRemovePlayer,
  players,
  registrationByCpf,
  title,
}: {
  adminPaymentMethod: PaymentMethod;
  isAdmin: boolean;
  onCopyPaymentLink: AdminSuper8GroupsProps["onCopyPaymentLink"];
  onDeleteRegistration: AdminSuper8GroupsProps["onDeleteRegistration"];
  onEditAvailabilityRestriction: AdminSuper8GroupsProps["onEditAvailabilityRestriction"];
  onMarkPaidRegistration: AdminSuper8GroupsProps["onMarkPaidRegistration"];
  onRejectRegistration: AdminSuper8GroupsProps["onRejectRegistration"];
  onRemovePlayer: AdminSuper8GroupsProps["onRemovePlayer"];
  players: DraftPlayer[];
  registrationByCpf: Record<string, AdminEventRegistration>;
  title: string;
}) {
  return (
    <div className="super8-group-card">
      <h4>{title}</h4>
      {players.length === 0 ? (
        <p className="vazio">
          {siteTexts.challengeEngine.labels.nonePlayerAdded}
        </p>
      ) : (
        <div className="super8-group-rows">
          {players.map((player) => {
            const cpf = normalizeCpf(player.cpf);
            return (
              <AdminSuper8PlayerRow
                key={`${title}-${cpf}`}
                adminPaymentMethod={adminPaymentMethod}
                isAdmin={isAdmin}
                onCopyPaymentLink={onCopyPaymentLink}
                onDeleteRegistration={onDeleteRegistration}
                onEditAvailabilityRestriction={onEditAvailabilityRestriction}
                onMarkPaidRegistration={onMarkPaidRegistration}
                onRejectRegistration={onRejectRegistration}
                onRemovePlayer={onRemovePlayer}
                player={player}
                registration={registrationByCpf[cpf] || null}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AdminSuper8Groups({
  adminPaymentMethod,
  groupAPlayers,
  isAdmin,
  onCopyPaymentLink,
  onDeleteRegistration,
  onEditAvailabilityRestriction,
  onMarkPaidRegistration,
  onRejectRegistration,
  onRemovePlayer,
  registrationByCpf,
  waitingPlayers,
}: AdminSuper8GroupsProps) {
  return (
    <>
      <div className="super8-groups-grid">
        <AdminSuper8GroupCard
          adminPaymentMethod={adminPaymentMethod}
          isAdmin={isAdmin}
          onCopyPaymentLink={onCopyPaymentLink}
          onDeleteRegistration={onDeleteRegistration}
          onEditAvailabilityRestriction={onEditAvailabilityRestriction}
          onMarkPaidRegistration={onMarkPaidRegistration}
          onRejectRegistration={onRejectRegistration}
          onRemovePlayer={onRemovePlayer}
          players={groupAPlayers}
          registrationByCpf={registrationByCpf}
          title={`Participantes do Super 8 (${groupAPlayers.length}/8)`}
        />
      </div>
      {waitingPlayers.length > 0 && (
        <div className="super8-group-card super8-waiting-card">
          <h4>Reserva ({waitingPlayers.length})</h4>
          <div className="super8-group-rows">
            {waitingPlayers.map((player) => {
              const cpf = normalizeCpf(player.cpf);
              return (
                <AdminSuper8PlayerRow
                  key={`super8-w-${cpf}`}
                  adminPaymentMethod={adminPaymentMethod}
                  isAdmin={isAdmin}
                  onCopyPaymentLink={onCopyPaymentLink}
                  onDeleteRegistration={onDeleteRegistration}
                  onEditAvailabilityRestriction={onEditAvailabilityRestriction}
                  onMarkPaidRegistration={onMarkPaidRegistration}
                  onRejectRegistration={onRejectRegistration}
                  onRemovePlayer={onRemovePlayer}
                  player={player}
                  registration={registrationByCpf[cpf] || null}
                />
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
